/**
 * DifferenceFeatureBlock Molecule Component
 * Compact feature block with icon, title, and bulleted list
 * Used in the "How We Make The Difference" section
 */

import React, { useState, useEffect } from "react";
import { Image } from "@/components/atoms/Image";
import { Heading } from "@/components/atoms/Heading";

export interface DifferenceFeatureBlockProps {
  icon?: string | { filename: string; alt?: string };
  icon_light?: { filename: string; alt?: string };
  title: string;
  description?: string;
  features?: string; // Multiline text, split by newlines
  className?: string;
}

export const DifferenceFeatureBlock: React.FC<DifferenceFeatureBlockProps> = ({
  icon,
  icon_light,
  title,
  description,
  features,
  className = "",
}) => {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // Get icon - handle both string and asset types
  const darkIconSrc = icon
    ? typeof icon === "string"
      ? icon
      : (icon as any).filename || ""
    : "";
  const lightIconSrc = icon_light?.filename || darkIconSrc;
  const iconSrc = isDark ? darkIconSrc : lightIconSrc;
  const iconAlt =
    icon && typeof icon !== "string" ? (icon as any).alt : "Feature Icon";

  // Convert features string (multiline text) to array
  const featureList = features
    ? features.split("\n").filter((f) => f.trim())
    : [];

  return (
    <div className={`flex flex-col gap-4 md:gap-5 ${className}`}>
      {/* Icon + Title Row */}
      <div className="flex items-center gap-3 md:gap-4">
        {iconSrc && (
          <div
            className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center`}
          >
            <Image
              src={iconSrc}
              alt={iconAlt}
              className="w-full h-full object-contain"
            />
          </div>
        )}
        <Heading level="h3" className="text-xl md:text-2xl text-theme-text">
          {title}
        </Heading>
      </div>

      {/* Bullet List */}
      {featureList.length > 0 && (
        <ul className="w-full space-y-3 md:space-y-4">
          {featureList.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-1 h-1 mt-1.5 rounded-full bg-black dark:bg-white" />
              <span className="font-space-mono text-sm leading-tight text-black dark:text-white">
                {feature}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Description (fallback if no features) */}
      {featureList.length === 0 && description && (
        <span className="font-space-mono text-sm leading-tight text-black dark:text-white">
          {description}
        </span>
      )}
    </div>
  );
};

export default DifferenceFeatureBlock;
